/**
 * Temporary gear and re-patches (docs/wiring-documentation-proposal.md,
 * "2. Temporary gear: owned by the config", "Re-patching for a production").
 *
 * A device or link with a `configID` belongs to that config and is in effect
 * only while the config is the studio's *effective config*: the active
 * config, or the original of the active scratch copy. The effective wiring
 * is the permanent devices and links plus the effective config's own; a
 * scoped link suspends the permanent links on either of its ports.
 *
 * Pure helpers on the Zero-shaped rows (`types/wiring.ts`), shared by the
 * mutators, the API, the bridge and the UI.
 */
import { deterministicUUID, deviceSubtree, describePort, } from "./wiring.js";
import { portSlotKey } from "./device-templates.js";
/**
 * The config whose temporary gear and re-patches are in effect while
 * `config` is active: the original of a scratch copy (scratch copies own no
 * gear), else the config itself.
 */
export function effectiveConfigIDOf(config) {
    if (!config)
        return null;
    if (config.kind === "scratch" && config.sourceConfigID) {
        return config.sourceConfigID;
    }
    return config.id;
}
/** Permanent (no config) or owned by the effective config. */
export function isInEffect(item, effectiveConfigID) {
    return !item.configID || item.configID === (effectiveConfigID ?? null);
}
/**
 * The wiring of a studio while `effectiveConfigID` is its effective config
 * (null: none, only permanent wiring). Each port is in at most one link of
 * the result: a scoped link suspends the permanent link(s) on either port.
 */
export function effectiveWiring(installation, effectiveConfigID) {
    const effective = effectiveConfigID ?? null;
    const devices = [];
    const inactiveDevices = [];
    for (const device of installation.devices) {
        if (isInEffect(device, effective))
            devices.push(device);
        else
            inactiveDevices.push(device);
    }
    const deviceIDs = new Set(devices.map((device) => device.id));
    const ports = installation.ports.filter((port) => deviceIDs.has(port.deviceID));
    const portIDs = new Set(ports.map((port) => port.id));
    const usable = (link) => portIDs.has(link.fromPortID) && portIDs.has(link.toPortID);
    const scoped = installation.links.filter((link) => !!link.configID && link.configID === effective && usable(link));
    const scopedByPort = new Map();
    for (const link of scoped) {
        scopedByPort.set(link.fromPortID, link);
        scopedByPort.set(link.toPortID, link);
    }
    const configName = installation.configs?.find((config) => config.id === effective)?.name ??
        "this config";
    const links = [];
    const suspendedLinks = [];
    for (const link of installation.links) {
        if (link.configID || !usable(link))
            continue;
        const by = [
            scopedByPort.get(link.fromPortID),
            scopedByPort.get(link.toPortID),
        ].filter((other, index, all) => {
            return !!other && all.indexOf(other) === index;
        });
        if (by.length > 0)
            suspendedLinks.push({ link, by });
        else
            links.push(link);
    }
    links.push(...scoped);
    const linkByPort = new Map();
    for (const link of links) {
        linkByPort.set(link.fromPortID, link);
        linkByPort.set(link.toPortID, link);
    }
    const suspendedPorts = new Map();
    for (const { link, by } of suspendedLinks) {
        for (const portID of [link.fromPortID, link.toPortID]) {
            if (linkByPort.has(portID) || suspendedPorts.has(portID))
                continue;
            suspendedPorts.set(portID, {
                portID,
                linkID: link.id,
                configID: by[0].configID,
                reason: `re-patched for ${configName}`,
            });
        }
    }
    return {
        effectiveConfigID: effective,
        devices,
        inactiveDevices,
        ports,
        links,
        suspendedLinks,
        suspendedPorts,
        linkByPort,
    };
}
/** The permanent links a scoped link (a re-patch) would suspend. */
export function linksSuspendedBy(link, links) {
    return links.filter((other) => !other.configID &&
        [other.fromPortID, other.toPortID].some((portID) => portID === link.fromPortID || portID === link.toPortID));
}
// ─── Physical status ───────────────────────────────────────────
export const PATCH_STATUS_LABELS = {
    pending_setup: "Pending set-up",
    patched: "Patched",
    pending_restore: "Pending restore",
    restored: "Restored",
};
/**
 * Status of a new scoped link: pending set-up while its config is in effect
 * (someone has to plug it), none while it is only planned.
 */
export function initialPatchStatus(configID, effectiveConfigID) {
    return configID === effectiveConfigID ? "pending_setup" : null;
}
/**
 * Status changes when the effective config switches from `previous` to
 * `next` (a load was reported): the new config's re-patches have to be set
 * up, the old config's ones restored. Loading never waits for the ticks.
 * Reloading the same effective config changes nothing.
 */
export function planPatchStatusSwitch(links, previous, next) {
    if ((previous ?? null) === (next ?? null))
        return [];
    const changes = [];
    for (const link of links) {
        if (!link.configID)
            continue;
        if (link.configID === next) {
            if (link.patchStatus !== "pending_setup") {
                changes.push({ link, patchStatus: "pending_setup" });
            }
        }
        else if (link.configID === previous) {
            if (link.patchStatus !== "pending_restore" &&
                link.patchStatus !== "restored") {
                changes.push({ link, patchStatus: "pending_restore" });
            }
        }
    }
    return changes;
}
/**
 * Re-patches not done yet: the effective config's links still to be set
 * up, and every config's links still to be restored. Tracing may be wrong
 * while there are any.
 */
export function openRepatches(links, effectiveConfigID) {
    const pendingSetup = links.filter((link) => !!link.configID &&
        link.configID === effectiveConfigID &&
        link.patchStatus === "pending_setup");
    const pendingRestore = links.filter((link) => !!link.configID && link.patchStatus === "pending_restore");
    return {
        pendingSetup,
        pendingRestore,
        count: pendingSetup.length + pendingRestore.length,
    };
}
export function openRepatchesText(count) {
    return `${count} re-patch${count === 1 ? "" : "es"} not done yet: tracing may be wrong`;
}
/** "A2" < "A10": patch points compared like humans read them. */
function naturalCompare(a, b) {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
}
/**
 * A config's patch sheet: its scoped links with panel positions, in set-up
 * order (by patch point, then destination) and in restore order (the
 * reverse, so the last cable plugged is the first one pulled).
 */
export function buildPatchSheet(installation, configID) {
    const portByID = new Map(installation.ports.map((port) => [port.id, port]));
    const describe = (portID) => {
        const port = portByID.get(portID);
        return port ? describePort(port, installation.devices) : "Unknown port";
    };
    const lines = installation.links
        .filter((link) => link.configID === configID)
        .map((link) => ({
        link,
        from: describe(link.fromPortID),
        to: describe(link.toPortID),
        patchPoint: link.patchPoint?.trim() || null,
        cable: link.cable?.trim() || null,
        notes: link.notes?.trim() || null,
        replaces: linksSuspendedBy(link, installation.links).map((other) => `${describe(other.fromPortID)} → ${describe(other.toPortID)}${other.patchPoint ? ` (${other.patchPoint})` : ""}`),
        status: link.patchStatus ?? null,
    }));
    const setup = [...lines].sort((a, b) => naturalCompare(a.patchPoint ?? "￿", b.patchPoint ?? "￿") ||
        naturalCompare(a.to, b.to) ||
        a.link.id.localeCompare(b.link.id));
    return { setup, restore: [...setup].reverse() };
}
/** Restore is done (ticked), or the link was never set up at all. */
export function isRestoreDone(link) {
    return link.patchStatus == null || link.patchStatus === "restored";
}
/**
 * "Remove temporary gear and re-patches of this config": removes only the
 * scoped links whose restore is ticked off, and the config's devices that
 * no remaining link touches. A forgotten cable stays documented as pending
 * restore.
 */
export function planScopedCleanup(installation, configID) {
    const own = installation.links.filter((link) => link.configID === configID);
    const linksToDelete = own.filter(isRestoreDone);
    const linksKept = own.filter((link) => !isRestoreDone(link));
    const keptPorts = new Set(linksKept.flatMap((link) => [link.fromPortID, link.toPortID]));
    const scoped = installation.devices.filter((device) => device.configID === configID);
    const scopedIDs = new Set(scoped.map((device) => device.id));
    const roots = scoped.filter((device) => !device.parentID || !scopedIDs.has(device.parentID));
    const devicesToDelete = [];
    const devicesKept = [];
    const portIDsToDelete = [];
    for (const root of roots) {
        const subtree = deviceSubtree(root.id, installation.devices);
        const subtreeSet = new Set(subtree);
        const subtreePorts = installation.ports
            .filter((port) => subtreeSet.has(port.deviceID))
            .map((port) => port.id);
        const devicesInTree = subtree
            .map((id) => installation.devices.find((device) => device.id === id))
            .filter((device) => !!device);
        if (subtreePorts.some((portID) => keptPorts.has(portID))) {
            devicesKept.push(...devicesInTree);
            continue;
        }
        devicesToDelete.push(...devicesInTree);
        portIDsToDelete.push(...subtreePorts);
    }
    return {
        linksToDelete,
        linksKept,
        devicesToDelete,
        portIDsToDelete,
        devicesKept,
    };
}
/**
 * Clone a config's temporary gear (devices, their ports) and its scoped
 * links for a copy into `targetConfigID`. IDs are derived from the target
 * config and the old ID, so the client's optimistic rows match the server's.
 *
 * Permanent ends of re-patches stay as they are within the same studio. For
 * another studio, `deviceMap` maps permanent devices (by name and driver,
 * see autoMapDevices) and their ports are matched by direction, group and
 * number; a re-patch without a counterpart is dropped and listed.
 */
export function planScopedGearClone(input) {
    const { source, sourceConfigID, targetConfigID, targetStudioID } = input;
    const scoped = source.devices.filter((device) => device.configID === sourceConfigID);
    const sameStudio = input.targetPorts === undefined && !input.deviceMap;
    const cloneID = (kind, id) => deterministicUUID("scoped-clone", targetConfigID, kind, id);
    const deviceMap = new Map();
    for (const device of scoped)
        deviceMap.set(device.id, cloneID("device", device.id));
    const mapPermanentDevice = (id) => {
        if (!id)
            return null;
        if (deviceMap.has(id))
            return deviceMap.get(id) ?? null;
        if (sameStudio)
            return id;
        return input.deviceMap?.get(id) ?? null;
    };
    const devices = scoped.map((device) => ({
        ...device,
        id: deviceMap.get(device.id),
        studioID: targetStudioID,
        configID: targetConfigID,
        parentID: mapPermanentDevice(device.parentID),
    }));
    const portMap = new Map();
    const scopedIDs = new Set(scoped.map((device) => device.id));
    const ports = [];
    for (const port of source.ports) {
        if (!scopedIDs.has(port.deviceID))
            continue;
        const id = cloneID("port", port.id);
        portMap.set(port.id, id);
        ports.push({
            ...port,
            id,
            studioID: targetStudioID,
            deviceID: deviceMap.get(port.deviceID),
        });
    }
    // Permanent ports of another studio: same device (mapped), same slot
    if (!sameStudio) {
        const targetBySlot = new Map();
        for (const port of input.targetPorts ?? []) {
            targetBySlot.set(`${port.deviceID}|${portSlotKey({
                direction: port.direction,
                group: port.group ?? null,
                number: port.number,
            })}`, port.id);
        }
        for (const port of source.ports) {
            if (scopedIDs.has(port.deviceID))
                continue;
            const device = input.deviceMap?.get(port.deviceID);
            if (!device)
                continue;
            const target = targetBySlot.get(`${device}|${portSlotKey({
                direction: port.direction,
                group: port.group ?? null,
                number: port.number,
            })}`);
            if (target)
                portMap.set(port.id, target);
        }
    }
    const mapPort = (id) => portMap.get(id) ?? (sameStudio ? id : undefined);
    const links = [];
    const droppedLinks = [];
    for (const link of source.links) {
        if (link.configID !== sourceConfigID)
            continue;
        const from = mapPort(link.fromPortID);
        const to = mapPort(link.toPortID);
        if (!from || !to) {
            droppedLinks.push(link);
            continue;
        }
        links.push({
            ...link,
            id: cloneID("link", link.id),
            studioID: targetStudioID,
            configID: targetConfigID,
            fromPortID: from,
            toPortID: to,
            // A copy is planned, not plugged; it gets its status when loaded
            patchStatus: null,
            verifiedAt: null,
            verifiedBy: null,
        });
    }
    return { devices, ports, links, deviceMap, portMap, droppedLinks };
}
